/**
 * File storage service for offline photo and document management.
 * Uses @capacitor/filesystem on native platforms, falls back gracefully on web.
 *
 * On web, all methods return null/false/0 so callers can use IndexedDB instead.
 * On native (iOS/Android), photos are stored in the app-private Data directory
 * under the "offline-photos" folder.
 */
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';

const isNative = Capacitor.isNativePlatform();
const PHOTO_DIR = 'offline-photos';

class FileStorageService {
  private initialized = false;

  /**
   * Initialize the filesystem directory structure.
   * Safe to call multiple times -- only runs once on native.
   */
  async init(): Promise<void> {
    if (this.initialized || !isNative) return;
    this.initialized = true;
    try {
      await Filesystem.mkdir({
        path: PHOTO_DIR,
        directory: Directory.Data,
        recursive: true,
      });
    } catch {
      // Directory may already exist -- safe to ignore
    }
  }

  /**
   * Save a photo blob to native filesystem.
   * Returns the file path (relative to Directory.Data) on native,
   * or null on web (use IndexedDB instead).
   */
  async savePhoto(id: string, blob: Blob): Promise<string | null> {
    if (!isNative) return null;
    await this.init();

    try {
      const base64 = await this.blobToBase64(blob);
      const ext = blob.type === 'image/png' ? 'png' : 'jpg';
      const path = `${PHOTO_DIR}/${id}.${ext}`;

      await Filesystem.writeFile({
        path,
        data: base64,
        directory: Directory.Data,
      });

      return path;
    } catch (error) {
      console.error('[FileStorage] Failed to save photo:', error);
      return null;
    }
  }

  /**
   * Read a photo from native filesystem.
   * Returns a Blob, or null if not found or on web.
   */
  async readPhoto(path: string): Promise<Blob | null> {
    if (!isNative) return null;

    try {
      const result = await Filesystem.readFile({
        path,
        directory: Directory.Data,
      });

      // result.data is a base64 string on native
      const response = await fetch(`data:image/jpeg;base64,${result.data}`);
      return response.blob();
    } catch {
      return null;
    }
  }

  /**
   * Get a displayable URI for a saved photo.
   * On native, returns a WebView-compatible URI (capacitor:// or http://localhost).
   * On web, returns null (use blob URLs instead).
   */
  async getPhotoUri(path: string): Promise<string | null> {
    if (!isNative) return null;

    try {
      const result = await Filesystem.getUri({
        path,
        directory: Directory.Data,
      });
      return Capacitor.convertFileSrc(result.uri);
    } catch {
      return null;
    }
  }

  /**
   * Delete a saved photo from native filesystem.
   * Returns true if deleted, false on failure or web.
   */
  async deletePhoto(path: string): Promise<boolean> {
    if (!isNative) return false;

    try {
      await Filesystem.deleteFile({
        path,
        directory: Directory.Data,
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get total size of cached photos in bytes.
   * Returns 0 on web.
   */
  async getCacheSize(): Promise<number> {
    if (!isNative) return 0;
    await this.init();

    try {
      const files = await Filesystem.readdir({
        path: PHOTO_DIR,
        directory: Directory.Data,
      });

      let totalSize = 0;
      for (const file of files.files) {
        totalSize += file.size || 0;
      }
      return totalSize;
    } catch {
      return 0;
    }
  }

  /**
   * Clear all cached photos from native filesystem.
   * Recreates the empty directory after clearing.
   */
  async clearPhotoCache(): Promise<void> {
    if (!isNative) return;

    try {
      await Filesystem.rmdir({
        path: PHOTO_DIR,
        directory: Directory.Data,
        recursive: true,
      });
      // Recreate empty directory
      await Filesystem.mkdir({
        path: PHOTO_DIR,
        directory: Directory.Data,
        recursive: true,
      });
    } catch {
      // Ignore errors -- directory may not exist yet
    }
  }

  /**
   * Check whether a photo file exists on native filesystem.
   * Returns false on web.
   */
  async photoExists(path: string): Promise<boolean> {
    if (!isNative) return false;

    try {
      await Filesystem.stat({
        path,
        directory: Directory.Data,
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Convert a Blob to a base64 string (without the data: prefix).
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        // Strip the data:...;base64, prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}

export const fileStorageService = new FileStorageService();
